import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Category } from "@/types";
import { getApiUrl } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

interface ActiveServicesProps {
  onCategorySelect?: (category: Category) => void;
  selectedCategoryId?: number | undefined;
  facilityId?: number;
}

export default function ActiveServices({
  onCategorySelect,
  selectedCategoryId,
  facilityId,
}: ActiveServicesProps) {
  const { user, token } = useAuth();
  const [activeCategories, setActiveCategories] = useState<Category[]>([]);

  // Fetch company categories
  const { data: companyCategories, isLoading } = useQuery({
    queryKey: ["facility-categories", facilityId],
    queryFn: async () => {
      if (!facilityId) {
        return [];
      }
      try {
        const url = getApiUrl(
          `facility-categories/by_facility/?facility_id=${facilityId}`,
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(
            `Failed to fetch company categories: ${response.status} ${errorText}`,
          );
        }

        const data = await response.json();

        // Ensure we return an array
        if (data && data.results && Array.isArray(data.results)) {
          return data.results;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          console.error("Unexpected company categories data format:", data);
          return [];
        }
      } catch (err) {
        console.error("Error in queryFn:", err);
        throw err;
      }
    },
    enabled: !!facilityId,
  });

  useEffect(() => {
    if (companyCategories) {
      // Filter only active categories
      const active = companyCategories
        .filter((category: Category) => category.is_active)
        .map((category: Category) => ({
          id: category.id,
          name: category?.category_details?.name?.toLowerCase() || "",
          is_active: category.is_active,
        }));

      setActiveCategories(active);
      onCategorySelect?.(active[0]);
    }
  }, [companyCategories]);

  // Handle category selection
  const handleCategoryClick = (category: Category) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-12">
        Loading services...
      </div>
    );
  }

  if (activeCategories.length === 0) {
    return (
      <div className="bg-muted p-4 rounded-lg text-center">
        No active services found. Please configure your services in company
        settings.
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex space-x-1 overflow-x-auto pb-2">
        {activeCategories.map((category: Category) => (
          <button
            key={category.id}
            onClick={() => handleCategoryClick(category)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${selectedCategoryId === category.id ? "bg-primary text-primary-foreground" : "bg-muted/30 hover:bg-muted/50 text-foreground"}`}
          >
            {category.name}
          </button>
        ))}
      </div>
    </div>
  );
}
